import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Instagram, Youtube, Facebook, Send } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      const whatsappText = `New enquiry from website:\n\nName: ${formData.name}\nEmail: ${formData.email}\nSubject: ${formData.subject}\n\nMessage:\n${formData.message}`;
      window.open(`https://wa.me/919820751189?text=${encodeURIComponent(whatsappText)}`, '_blank');
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We will get back to you soon.",
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Something went wrong",
        description: "Your message could not be sent. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-ivory to-cream py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl md:text-6xl font-bold text-maroon mb-4">Get in Touch</h1>
          <p className="text-lg text-maroon-dark max-w-2xl mx-auto">
            Have questions or want to book a performance? We'd love to hear from you
          </p>
          <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8 fade-in">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-maroon mb-6">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-gold" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-maroon mb-1">Email</h3>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-maroon-dark hover:text-maroon transition-colors duration-200"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-gold" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-maroon mb-1">Phone</h3>
                      <a 
                        href={`tel:${contactInfo.phone}`}
                        className="text-maroon-dark hover:text-maroon transition-colors duration-200"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-maroon mb-6">Follow Us</h2>
                <p className="text-maroon-dark mb-6">
                  Stay connected and follow our journey on social media
                </p>
                <div className="flex gap-4">
                  <a
                    href={contactInfo.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center text-ivory hover:bg-maroon-dark transition-colors duration-200"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href={contactInfo.socialMedia.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center text-ivory hover:bg-maroon-dark transition-colors duration-200"
                    aria-label="YouTube"
                  >
                    <Youtube size={20} />
                  </a>
                  <a
                    href={contactInfo.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center text-ivory hover:bg-maroon-dark transition-colors duration-200"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-br from-maroon to-maroon-dark text-ivory rounded-lg shadow-lg p-8">
                <h3 className="text-xl font-bold mb-4">Performance Bookings</h3>
                <p className="text-ivory/90 mb-4 leading-relaxed">
                  Interested in booking a performance for your event? We offer solo and group presentations 
                  for corporate events, cultural festivals, weddings, and private functions.
                </p>
                <p className="text-gold text-sm">
                  Contact us for availability and custom packages.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="fade-in">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold text-maroon mb-6">Send a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-maroon">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-maroon">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-maroon">Subject</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="What is this regarding?"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-maroon">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="mt-2"
                      placeholder="Tell us more about your inquiry..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-maroon text-ivory hover:bg-maroon-dark py-6 text-base font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
                    data-testid="contact-submit-button"
                  >
                    <Send size={20} />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
